import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  TextField,
  TablePagination,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getInvolvements, getMinistryInvolvements, getMinistryAreaInvolvements, getMinistries, getMinistryMinistryAreas, getMinistryAreas, deleteInvolvement, exportInvolvementsCsv, updatePreferenceIsInRotation } from '../api';
import { useAuth } from '../context/AuthContext';

function formatPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

function formatPhoneForTable(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

function formatPhoneForSms(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') return digits.slice(1);
  return digits;
}

export default function InvolvementsPage() {
  const { ministryId: ministryIdParam, ministryAreaId: ministryAreaIdParam } = useParams();
  const [selectedMinistry, setSelectedMinistry] = useState(ministryIdParam || 'all');
  const [selectedMinistryArea, setSelectedMinistryArea] = useState(ministryAreaIdParam || 'all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [involvements, setInvolvements] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [ministryAreas, setMinistryAreas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { isAdmin } = useAuth();

  const loadMinistries = async () => {
    const data = await getMinistries();
    setMinistries(data);
  };

  const loadMinistryAreas = useCallback(async () => {
    try {
      const data =
        selectedMinistry === 'all'
          ? await getMinistryAreas()
          : await getMinistryMinistryAreas(selectedMinistry);
      setMinistryAreas(data);
    } catch {
      setMinistryAreas([]);
    }
  }, [selectedMinistry]);

  const loadInvolvements = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        pageSize,
        search: search || undefined,
        sortBy: 'created_at',
        sortOrder: 'desc',
      };
      let result;
      if (selectedMinistry !== 'all' && selectedMinistryArea !== 'all') {
        result = await getMinistryAreaInvolvements(selectedMinistry, selectedMinistryArea, params);
      } else if (selectedMinistry !== 'all') {
        result = await getMinistryInvolvements(selectedMinistry, {
          ...params,
          ministryAreaId: selectedMinistryArea !== 'all' ? selectedMinistryArea : undefined,
        });
      } else {
        result = await getInvolvements({
          ...params,
          ministryId: undefined,
          ministryAreaId: selectedMinistryArea !== 'all' ? selectedMinistryArea : undefined,
        });
      }
      const { data, meta } = result;
      setInvolvements(data);
      setTotal(meta?.total ?? 0);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
      setInvolvements([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMinistry, selectedMinistryArea, search, page, pageSize]);

  useEffect(() => {
    loadMinistries();
  }, []);

  useEffect(() => {
    if (ministryIdParam) {
      setSelectedMinistry(ministryIdParam);
    }
  }, [ministryIdParam]);

  useEffect(() => {
    if (ministryAreaIdParam) {
      setSelectedMinistryArea(ministryAreaIdParam);
    }
  }, [ministryAreaIdParam]);

  useEffect(() => {
    loadMinistryAreas();
  }, [loadMinistryAreas]);

  useEffect(() => {
    if (selectedMinistry !== 'all' && selectedMinistryArea !== 'all') {
      const ministryArea = ministryAreas.find((m) => String(m.id) === selectedMinistryArea);
      if (!ministryArea && ministryAreas.length > 0) {
        setSelectedMinistryArea('all');
      }
    }
  }, [ministryAreas, selectedMinistry, selectedMinistryArea]);

  useEffect(() => {
    loadInvolvements();
  }, [loadInvolvements]);

  const handleDelete = (inv) => setDeleteConfirm(inv);

  const handleSetInRotation = async (inv, isInRotation) => {
    try {
      await updatePreferenceIsInRotation(inv.id, isInRotation);
      setSnackbar({ open: true, message: isInRotation ? 'Added to rotation' : 'Removed from rotation', severity: 'success' });
      loadInvolvements();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleExport = async () => {
    if (selectedMinistry === 'all') return;
    try {
      if (selectedMinistryArea !== 'all') {
        await exportInvolvementsCsv(selectedMinistry, selectedMinistryArea);
      } else {
        await exportInvolvementsCsv(selectedMinistry);
      }
      setSnackbar({ open: true, message: 'Export downloaded', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handlePrint = async () => {
    if (selectedMinistryArea === 'all') return;
    try {
      const params = { page: 1, pageSize: 1000, sortBy: 'created_at', sortOrder: 'desc' };
      let result;
      if (selectedMinistry !== 'all') {
        result = await getMinistryAreaInvolvements(selectedMinistry, selectedMinistryArea, params);
      } else {
        result = await getInvolvements({
          ...params,
          ministryAreaId: selectedMinistryArea,
        });
      }
      const { data: printInvolvements } = result;
      if (!printInvolvements?.length) {
        setSnackbar({ open: true, message: 'No involvements to print', severity: 'info' });
        return;
      }
      const ministryName = printInvolvements[0]?.ministry?.name ?? ministries.find((m) => String(m.id) === selectedMinistry)?.name ?? 'Ministry';
      const ministryAreaName = printInvolvements[0]?.ministry_area?.name ?? ministryAreas.find((m) => String(m.id) === selectedMinistryArea)?.name ?? 'Ministry Area';
      const phones = [...new Set(printInvolvements.map((inv) => inv.member?.phone && formatPhoneForSms(inv.member.phone)).filter(Boolean))];
      const emails = [...new Set(printInvolvements.map((inv) => inv.member?.email).filter(Boolean))];
      const sortedInvolvements = [...printInvolvements].sort((a, b) =>
        (a.member?.last_name ?? '').localeCompare(b.member?.last_name ?? '', undefined, { sensitivity: 'base' })
      );
      const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${ministryName} - ${ministryAreaName}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1, h3 { text-align: center; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { border: 1px solid #333; padding: 8px; text-align: left; }
    th { background: #f0f0f0; }
    .copy-section { margin: 20px 0; }
    .copy-section h5 { margin-bottom: 8px; }
    .copy-section pre { background: #f5f5f5; padding: 10px; overflow-x: auto; white-space: pre-wrap; word-wrap: break-word; }
  </style>
</head>
<body>
  <h1>${ministryName}</h1>
  <h3>${ministryAreaName}</h3>
  <table>
    <thead>
      <tr><th>First</th><th>Last</th><th>Phone</th><th>Email</th></tr>
    </thead>
    <tbody>
      ${sortedInvolvements
        .map(
          (inv) =>
            `<tr>
              <td>${inv.member?.first_name ?? '-'}</td>
              <td>${inv.member?.last_name ?? '-'}</td>
              <td>${inv.member?.phone ? formatPhoneForTable(inv.member.phone) : '-'}</td>
              <td>${inv.member?.email ?? '-'}</td>
            </tr>`
        )
        .join('')}
    </tbody>
  </table>
  <div class="copy-section">
    <h5>Phone Numbers Only</h5>
    <pre>${phones.join(', ')}</pre>
  </div>
  <div class="copy-section">
    <h5>Emails Only</h5>
    <pre>${emails.join(', ')}</pre>
  </div>
</body>
</html>`;
      const win = window.open('', '_blank');
      win.document.write(printContent);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 250);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteInvolvement(deleteConfirm.id);
      setSnackbar({ open: true, message: 'Involvement deleted', severity: 'success' });
      setDeleteConfirm(null);
      loadInvolvements();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5">Member Preferences</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ministry</InputLabel>
            <Select
              value={selectedMinistry}
              label="Ministry"
              onChange={(e) => {
                setSelectedMinistry(e.target.value);
                setSelectedMinistryArea('all');
                setPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {ministries.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Ministry Area</InputLabel>
            <Select
              value={selectedMinistryArea}
              label="Ministry Area"
              onChange={(e) => {
                setSelectedMinistryArea(e.target.value);
                setPage(0);
              }}
              disabled={selectedMinistry === 'all' && ministryAreas.length === 0}
            >
              <MenuItem value="all">All</MenuItem>
              {ministryAreas.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          size="small"
          placeholder="Search... (press Enter)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput);
              setPage(0);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={selectedMinistryArea === 'all'}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={selectedMinistry === 'all'}
        >
          Export
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Ministry</TableCell>
              <TableCell>Ministry Area</TableCell>
              <TableCell>In Rotation</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Created</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>Loading...</TableCell>
              </TableRow>
            ) : involvements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>No involvements found</TableCell>
              </TableRow>
            ) : (
              involvements.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell>{inv.member?.first_name ?? '-'}</TableCell>
                  <TableCell>{inv.member?.last_name ?? '-'}</TableCell>
                  <TableCell>{inv.ministry?.name ?? '-'}</TableCell>
                  <TableCell>{inv.ministry_area?.name ?? '-'}</TableCell>
                  <TableCell>
                    {inv.is_in_rotation !== false ? (
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} titleAccess="In rotation" />
                    ) : (
                      <CloseIcon sx={{ color: 'error.main', fontSize: 20 }} titleAccess="Not in rotation" />
                    )}
                  </TableCell>
                  <TableCell>{inv.member?.phone ? formatPhone(inv.member.phone) : '-'}</TableCell>
                  <TableCell>{inv.member?.email ?? '-'}</TableCell>
                  <TableCell>{formatDate(inv.created_at)}</TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Tooltip
                        title={
                          inv.is_in_rotation === false
                            ? 'Currently the member is not in the rotation for this ministry area. Clicking this will add the member to the rotation for this ministry area.'
                            : 'Already in rotation'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleSetInRotation(inv, true)}
                            disabled={inv.is_in_rotation !== false}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          inv.is_in_rotation !== false
                            ? 'Currently the member is in the rotation for this ministry area. Clicking this will remove the member from the rotation for this ministry area.'
                            : 'Not in rotation'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleSetInRotation(inv, false)}
                            disabled={inv.is_in_rotation === false}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton size="small" color="error" onClick={() => handleDelete(inv)} title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
        />
      </TableContainer>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Interest</DialogTitle>
        <DialogContent>
          Are you sure you want to remove {deleteConfirm?.member?.first_name} {deleteConfirm?.member?.last_name}{' '}
          from {deleteConfirm?.ministry_area?.name}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
